import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const getCurrentUser = () => localStorage.getItem('notes_user') || '';

async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_URL}${url}`, { ...options, headers: { ...(options.headers || {}), 'x-token': token } });
  if (res.status === 401) { localStorage.removeItem('notes_token'); localStorage.removeItem('notes_user'); alert('登录超时，请重新登录'); window.location.href = '/'; return null; }
  return res;
}

export default function AnnouncementManager() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', attachments: [] });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    setLoading(true);
    const res = await fetchWithAuth('/api/admin/announcements');
    if (!res) return;
    const data = await res.json();
    if (data.success) setList(data.list);
    setLoading(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetchWithAuth('/api/upload', { method: 'POST', body: formData });
    if (!res) return;
    const data = await res.json();
    if (data.success) {
      setForm({ ...form, attachments: [...form.attachments, { name: file.name, url: data.url, cosUrl: data.cosUrl, size: file.size }] });
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setForm({ ...form, attachments: form.attachments.filter((_, i) => i !== idx) });
  };

  const saveAnnouncement = async () => {
    if (!form.title.trim()) return alert('请输入公告标题');
    if (!form.content.trim() && form.attachments.length === 0) return alert('请输入公告内容或上传附件');
    
    const res = await fetchWithAuth(editing ? `/api/admin/announcements/${editing}` : '/api/admin/announcements', {
      method: editing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    if (!res) return;
    const data = await res.json();
    if (data.success) {
      setEditing(null);
      setForm({ title: '', content: '', attachments: [] });
      fetchList();
    } else {
      alert('保存失败: ' + (data.error || '未知错误'));
    }
  };

  const editItem = (item) => {
    setEditing(item.id);
    setForm({ title: item.title, content: item.content || '', attachments: item.attachments || [] });
  };

  const deleteItem = async (id) => {
    if (!window.confirm('确定要删除这条公告吗？')) return;
    const res = await fetchWithAuth(`/api/admin/announcements/${id}`, { method: 'DELETE' });
    if (!res) return;
    const data = await res.json();
    if (data.success) fetchList();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + 'B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
    return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
  };

  if (loading) return <div className="loading-state">加载中...</div>;

  return (
    <div className="page-container">
      <h2 style={{ marginBottom: 24 }}>📢 公告管理</h2>
      
      {/* 发布/编辑区域 */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, marginBottom: 24, border: '1px solid #e8e8e8' }}>
        <h3 style={{ fontSize: 16, marginBottom: 16 }}>{editing ? '编辑公告' : '发布新公告'}</h3>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>公告标题</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="输入公告标题" style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 14 }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>公告内容</label>
          <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="输入公告内容，支持换行" rows={6} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 14, resize: 'vertical' }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>附件</label>
          <input type="file" onChange={handleUpload} disabled={uploading} style={{ marginBottom: 8 }} />
          {form.attachments.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {form.attachments.map((att, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fafafa', borderRadius: 6, marginBottom: 4 }}>
                  <span style={{ flex: 1 }}>📄 {att.name} <span style={{ color: '#999', fontSize: 12 }}>({formatSize(att.size)})</span></span>
                  <button onClick={() => removeAttachment(idx)} style={{ padding: '2px 8px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>删除</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={saveAnnouncement} style={{ padding: '10px 24px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
            {editing ? '更新公告' : '发布公告'}
          </button>
          {editing && (
            <button onClick={() => { setEditing(null); setForm({ title: '', content: '', attachments: [] }); }} style={{ padding: '10px 24px', background: '#f0f0f0', border: '1px solid #d9d9d9', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
              取消
            </button>
          )}
        </div>
      </div>
      
      {/* 公告列表 */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8e8e8' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e8e8e8', fontWeight: 500 }}>已发布公告</div>
        {list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>暂无公告</div>
        ) : (
          list.map(item => (
            <div key={item.id} style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 16 }}>{item.title}</h4>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                    {new Date(item.createdAt).toLocaleString('zh-CN')}
                    {item.updatedAt && <span style={{ marginLeft: 12, color: '#999' }}>(已编辑)</span>}
                  </div>
                  {item.content && <p style={{ margin: '0 0 8px 0', fontSize: 14, whiteSpace: 'pre-wrap' }}>{item.content}</p>}
                  {item.attachments && item.attachments.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {item.attachments.map((att, idx) => (
                        <a key={idx} href={att.cosUrl || att.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginRight: 12, padding: '4px 12px', background: '#f0f5ff', color: '#2f54eb', borderRadius: 4, fontSize: 12, textDecoration: 'none' }}>
                          📎 {att.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => editItem(item)} style={{ padding: '4px 12px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>编辑</button>
                  <button onClick={() => deleteItem(item.id)} style={{ padding: '4px 12px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>删除</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}